/** App URL navigation helpers for deeplinks and browser back/forward. */

export interface PushAppUrlOptions {
  replace?: boolean;
}

export function pushAppUrl(url: string, options?: PushAppUrlOptions): void {
  if (options?.replace) {
    history.replaceState({}, '', url);
  } else {
    history.pushState({}, '', url);
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** True when the search string contains `create=1`. */
export function hasCreateIntent(search: string): boolean {
  return new URLSearchParams(search).get('create') === '1';
}

/** Remove `create` from a URL's search while preserving other params. */
export function stripCreateIntent(pathname: string, search: string, hash = ''): string {
  const params = new URLSearchParams(search);
  params.delete('create');
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ''}${hash}`;
}

/** True when `?create=1` was present; strips it from the URL without navigating. */
export function consumeCreateIntent(): boolean {
  if (!hasCreateIntent(window.location.search)) {
    return false;
  }
  history.replaceState(
    history.state,
    '',
    stripCreateIntent(window.location.pathname, window.location.search, window.location.hash),
  );
  return true;
}

export function onAppNavigate(listener: () => void): () => void {
  window.addEventListener('popstate', listener);
  return () => window.removeEventListener('popstate', listener);
}

/** Read manifest subfolder path from `/files/{id}?path=...`. */
export function readFilesBrowsePath(): string {
  const raw = new URLSearchParams(window.location.search).get('path');
  if (!raw) {
    return '';
  }
  try {
    return decodeURIComponent(raw).replace(/^\/+|\/+$/g, '');
  } catch {
    return '';
  }
}

export function buildFilesFolderUrl(folderId: string, path = ''): string {
  const base = `/files/${folderId}`;
  const normalized = path.replace(/^\/+|\/+$/g, '');
  if (!normalized) {
    return base;
  }
  return `${base}?path=${encodeURIComponent(normalized)}`;
}

export type SedrRoomTab =
  | 'dashboard'
  | 'phases'
  | 'compliance'
  | 'evidence'
  | 'access'
  | 'reports'
  | 'audit'
  | 'settings';

export interface SedrRoomUrlState {
  roomId: string;
  tab: SedrRoomTab;
  requirementId: string | null;
  evidenceFolderId: string | null;
}

const SEDR_TAB_SEGMENTS: SedrRoomTab[] = [
  'dashboard',
  'phases',
  'compliance',
  'evidence',
  'access',
  'reports',
  'audit',
  'settings',
];

export function parseSedrRoomUrl(path = window.location.pathname): SedrRoomUrlState | null {
  const match = path.match(/^\/sedr\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?$/);
  if (!match) {
    return null;
  }

  const roomId = match[1];
  const segment = match[2] as SedrRoomTab | undefined;
  const third = match[3] ?? null;

  if (!segment) {
    return { roomId, tab: 'dashboard', requirementId: null, evidenceFolderId: null };
  }

  if (!SEDR_TAB_SEGMENTS.includes(segment)) {
    return { roomId, tab: 'dashboard', requirementId: null, evidenceFolderId: null };
  }

  if (segment === 'compliance') {
    return { roomId, tab: 'compliance', requirementId: third, evidenceFolderId: null };
  }

  if (segment === 'evidence') {
    return { roomId, tab: 'evidence', requirementId: null, evidenceFolderId: third };
  }

  return { roomId, tab: segment, requirementId: null, evidenceFolderId: null };
}

export function buildSedrRoomUrl(
  roomId: string,
  tab: SedrRoomTab = 'dashboard',
  options?: { requirementId?: string | null; evidenceFolderId?: string | null },
): string {
  const base = `/sedr/${roomId}`;
  if (tab === 'dashboard') {
    return base;
  }
  if (tab === 'compliance') {
    const reqId = options?.requirementId;
    return reqId ? `${base}/compliance/${reqId}` : `${base}/compliance`;
  }
  if (tab === 'evidence') {
    const folderId = options?.evidenceFolderId;
    return folderId ? `${base}/evidence/${folderId}` : `${base}/evidence`;
  }
  return `${base}/${tab}`;
}

export function readSedrRoomUrlState(): SedrRoomUrlState | null {
  return parseSedrRoomUrl(window.location.pathname);
}

/** Guest-facing room tabs (subset of member SEDR tabs). */
export type GuestSedrRoomTab = 'dashboard' | 'compliance' | 'evidence' | 'audit';

export interface GuestSedrRoomUrlState {
  roomId: string;
  tab: GuestSedrRoomTab;
  requirementId: string | null;
  evidenceFolderId: string | null;
}

const GUEST_SEDR_TAB_SEGMENTS: GuestSedrRoomTab[] = [
  'dashboard',
  'compliance',
  'evidence',
  'audit',
];

export function parseGuestSedrRoomUrl(path = window.location.pathname): GuestSedrRoomUrlState | null {
  const match = path.match(/^\/guest\/rooms\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?\/?$/);
  if (!match) {
    return null;
  }

  const roomId = decodeURIComponent(match[1]);
  const segment = match[2] as GuestSedrRoomTab | undefined;
  const third = match[3] ? decodeURIComponent(match[3]) : null;

  if (!segment) {
    return { roomId, tab: 'dashboard', requirementId: null, evidenceFolderId: null };
  }

  if (!GUEST_SEDR_TAB_SEGMENTS.includes(segment)) {
    return { roomId, tab: 'dashboard', requirementId: null, evidenceFolderId: null };
  }

  if (segment === 'compliance') {
    return { roomId, tab: 'compliance', requirementId: third, evidenceFolderId: null };
  }

  if (segment === 'evidence') {
    return { roomId, tab: 'evidence', requirementId: null, evidenceFolderId: third };
  }

  return { roomId, tab: segment, requirementId: null, evidenceFolderId: null };
}

export function buildGuestSedrRoomUrl(
  roomId: string,
  tab: GuestSedrRoomTab = 'dashboard',
  options?: { requirementId?: string | null; evidenceFolderId?: string | null },
): string {
  const base = `/guest/rooms/${encodeURIComponent(roomId)}`;
  if (tab === 'dashboard') {
    return base;
  }
  if (tab === 'compliance') {
    const reqId = options?.requirementId;
    return reqId ? `${base}/compliance/${encodeURIComponent(reqId)}` : `${base}/compliance`;
  }
  if (tab === 'evidence') {
    const folderId = options?.evidenceFolderId;
    return folderId ? `${base}/evidence/${encodeURIComponent(folderId)}` : `${base}/evidence`;
  }
  return `${base}/${tab}`;
}

export function readGuestSedrRoomUrlState(): GuestSedrRoomUrlState | null {
  return parseGuestSedrRoomUrl(window.location.pathname);
}

/** Map a member `/sedr/...` room deep link to the guest portal equivalent. */
export function sedrRoomUrlToGuestRoomUrl(path = window.location.pathname): string | null {
  const state = parseSedrRoomUrl(path);
  if (!state) {
    return null;
  }

  const tab: GuestSedrRoomTab =
    state.tab === 'compliance' || state.tab === 'evidence' || state.tab === 'audit'
      ? state.tab
      : 'dashboard';

  return buildGuestSedrRoomUrl(state.roomId, tab, {
    requirementId: tab === 'compliance' ? state.requirementId : null,
    evidenceFolderId: tab === 'evidence' ? state.evidenceFolderId : null,
  });
}

export type SedrLibraryTab = 'requirements' | 'folders';

export interface SedrLibraryUrlState {
  tab: SedrLibraryTab;
  setId: string | null;
}

export function isSedrLibraryPath(path = window.location.pathname): boolean {
  return path === '/sedr/library' || path.startsWith('/sedr/library/');
}

export function parseSedrLibraryUrl(path = window.location.pathname): SedrLibraryUrlState | null {
  if (path === '/sedr/library' || path === '/sedr/library/') {
    return { tab: 'requirements', setId: null };
  }

  const match = path.match(/^\/sedr\/library\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (!match) {
    return null;
  }

  const first = decodeURIComponent(match[1]);
  const second = match[2] ? decodeURIComponent(match[2]) : null;

  // Legacy /sedr/library/categories → requirements sets.
  if (first === 'categories') {
    return { tab: 'requirements', setId: null };
  }

  if (first === 'requirements' || first === 'folders') {
    return { tab: first, setId: second };
  }

  // Legacy /sedr/library/{setId} → requirements set detail.
  return { tab: 'requirements', setId: first };
}

export function buildSedrLibraryUrl(options?: {
  tab?: SedrLibraryTab;
  setId?: string | null;
}): string {
  const tab = options?.tab ?? 'requirements';
  const setId = options?.setId;
  if (setId) {
    return `/sedr/library/${tab}/${encodeURIComponent(setId)}`;
  }
  return `/sedr/library/${tab}`;
}

export function readSedrLibraryUrlState(): SedrLibraryUrlState | null {
  return parseSedrLibraryUrl(window.location.pathname);
}

export type AdminSection =
  | 'system'
  | 'disk'
  | 'replyBacklog'
  | 'settings'
  | 'passwordPolicies'
  | 'audit'
  | 'recovery'
  | 'emergencyRevoke';

export interface AdminUrlState {
  /** Null means bare `/admin` (default section). */
  section: AdminSection | null;
}

const ADMIN_SECTION_BY_SEGMENT: Record<string, AdminSection> = {
  system: 'system',
  disk: 'disk',
  'reply-backlog': 'replyBacklog',
  settings: 'settings',
  'password-policies': 'passwordPolicies',
  audit: 'audit',
  recovery: 'recovery',
  'emergency-revoke': 'emergencyRevoke',
};

const ADMIN_SEGMENT_BY_SECTION: Record<AdminSection, string> = {
  system: 'system',
  disk: 'disk',
  replyBacklog: 'reply-backlog',
  settings: 'settings',
  passwordPolicies: 'password-policies',
  audit: 'audit',
  recovery: 'recovery',
  emergencyRevoke: 'emergency-revoke',
};

export function isAdminModulePath(path = window.location.pathname): boolean {
  return path === '/admin' || path.startsWith('/admin/');
}

export function parseAdminUrl(path = window.location.pathname): AdminUrlState | null {
  const normalized = path.replace(/\/+$/, '') || '/';
  if (normalized === '/admin') {
    return { section: null };
  }

  const match = normalized.match(/^\/admin\/([^/]+)$/);
  if (!match) {
    return null;
  }

  const section = ADMIN_SECTION_BY_SEGMENT[match[1]];
  if (!section) {
    return { section: null };
  }

  return { section };
}

export function buildAdminUrl(
  section: AdminSection | null = null,
  options?: { search?: string },
): string {
  const path = section ? `/admin/${ADMIN_SEGMENT_BY_SECTION[section]}` : '/admin';
  if (section !== 'audit') {
    return path;
  }

  const search = options?.search ?? '';
  const qs = search.startsWith('?') ? search.slice(1) : search.replace(/^\?/, '');
  return qs ? `${path}?${qs}` : path;
}

export function readAdminUrlState(): AdminUrlState | null {
  return parseAdminUrl(window.location.pathname);
}

export type SettingsSection = 'passphrase' | 'recovery' | 'webauthn' | 'notifications';

export type GuestSettingsSection = 'passphrase' | 'recovery' | 'notifications' | 'totp';

export interface SettingsUrlState {
  /** Null means bare `/settings` (default section). */
  section: SettingsSection | null;
}

export interface GuestSettingsUrlState {
  section: GuestSettingsSection | null;
}

const SETTINGS_SECTION_BY_SEGMENT: Record<string, SettingsSection> = {
  passphrase: 'passphrase',
  recovery: 'recovery',
  webauthn: 'webauthn',
  notifications: 'notifications',
};

const SETTINGS_SEGMENT_BY_SECTION: Record<SettingsSection, string> = {
  passphrase: 'passphrase',
  recovery: 'recovery',
  webauthn: 'webauthn',
  notifications: 'notifications',
};

const GUEST_SETTINGS_SECTION_BY_SEGMENT: Record<string, GuestSettingsSection> = {
  passphrase: 'passphrase',
  recovery: 'recovery',
  notifications: 'notifications',
  totp: 'totp',
};

const GUEST_SETTINGS_SEGMENT_BY_SECTION: Record<GuestSettingsSection, string> = {
  passphrase: 'passphrase',
  recovery: 'recovery',
  notifications: 'notifications',
  totp: 'totp',
};

export function isSettingsModulePath(path = window.location.pathname): boolean {
  return path === '/settings' || path.startsWith('/settings/');
}

export function isGuestSettingsPath(path = window.location.pathname): boolean {
  return path === '/guest/settings' || path.startsWith('/guest/settings/');
}

export function parseSettingsUrl(path = window.location.pathname): SettingsUrlState | null {
  const normalized = path.replace(/\/+$/, '') || '/';
  if (normalized === '/settings') {
    return { section: null };
  }

  const match = normalized.match(/^\/settings\/([^/]+)$/);
  if (!match) {
    return null;
  }

  const section = SETTINGS_SECTION_BY_SEGMENT[match[1]];
  if (!section) {
    return { section: null };
  }

  return { section };
}

export function buildSettingsUrl(section: SettingsSection | null = null): string {
  return section ? `/settings/${SETTINGS_SEGMENT_BY_SECTION[section]}` : '/settings';
}

export function readSettingsUrlState(): SettingsUrlState | null {
  return parseSettingsUrl(window.location.pathname);
}

export function parseGuestSettingsUrl(path = window.location.pathname): GuestSettingsUrlState | null {
  const normalized = path.replace(/\/+$/, '') || '/';
  if (normalized === '/guest/settings') {
    return { section: null };
  }

  const match = normalized.match(/^\/guest\/settings\/([^/]+)$/);
  if (!match) {
    return null;
  }

  const section = GUEST_SETTINGS_SECTION_BY_SEGMENT[match[1]];
  if (!section) {
    return { section: null };
  }

  return { section };
}

export function buildGuestSettingsUrl(section: GuestSettingsSection | null = null): string {
  return section
    ? `/guest/settings/${GUEST_SETTINGS_SEGMENT_BY_SECTION[section]}`
    : '/guest/settings';
}
